import { API_URLS } from "@solomon-apps/shared";
import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

interface RetailClaimSummary {
    hasClaimable: boolean;
    canClaim: boolean;
    status: "fresh" | "unavailable";
    preparationEnabled: boolean;
}
export function useRetailClaimSummary(address: string): UseQueryResult<RetailClaimSummary, Error> {
    return useQuery({
        queryKey: ["retail-claim-summary", API_URLS.YAAS, address],
        enabled: Boolean(address),
        queryFn: async ({ signal }): Promise<RetailClaimSummary> => {
            const response = await fetch(
                `${API_URLS.YAAS}/api/yaas/wallet/${encodeURIComponent(address)}?view=claim-summary`,
                { signal }
            );
            if (!response.ok) throw new Error("Could not check claimable rewards");
            const value: unknown = await response.json();
            if (
                !value ||
                typeof value !== "object" ||
                !("hasClaimable" in value) ||
                typeof value.hasClaimable !== "boolean" ||
                !("canClaim" in value) ||
                typeof value.canClaim !== "boolean" ||
                !("status" in value) ||
                !["fresh", "unavailable"].includes(String(value.status)) ||
                !("preparationEnabled" in value) ||
                typeof value.preparationEnabled !== "boolean"
            )
                throw new Error("Could not check claimable rewards");
            return value as RetailClaimSummary;
        },
        // Refresh on page return and focus; completion also calls refetch.
        // An idle wallet does not need a repeating availability request.
        refetchInterval: false,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        staleTime: 5_000
    });
}
